/**
 * Tâche 1144 (Eric 2026-06-16) — Cadence de remise des alertes.
 *
 * Un incident ouvert le 4 septembre était encore rappelé des semaines plus
 * tard : 5 176 occurrences, un courriel par rappel. À ce rythme on apprend à
 * ignorer les alertes. Par défaut : critique tout de suite, le reste dans un
 * résumé quotidien. Chaque organisation peut forcer l'un ou l'autre.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClockCounterClockwise, PaperPlaneTilt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_LABELS = {
  smart: "Critique tout de suite, le reste en résumé quotidien (recommandé)",
  immediate: "Tout tout de suite — un courriel par évènement",
  digest: "Tout dans le résumé quotidien, même les incidents critiques",
};

export const AlertDeliveryModeCard = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/system-notifications/digest`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const setMode = async (mode) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/api/health-monitor/settings`,
        { alert_delivery_mode: mode },
        { withCredentials: true }
      );
      await load();
      toast.success("Cadence de remise enregistrée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/system-notifications/digest/send-now`,
        {},
        { withCredentials: true }
      );
      await load();
      if (res.data?.envois)
        toast.success(`Résumé envoyé (${res.data.envois} courriel(s))`);
      else
        toast.info(
          "Rien envoyé : alertes en pause, sans destinataire, ou environnement sans courriel"
        );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const waiting = data.pending || [];

  return (
    <div
      className="rounded-xl border border-zinc-200 p-4 mb-4"
      data-testid="alert-delivery-mode"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold text-zinc-900">
            Cadence de remise des alertes
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Un incident qui dure envoyait un rappel par heure. Regroupez-les en un
            seul courriel quotidien — les incidents critiques, eux, partent
            immédiatement.
          </p>
        </div>
        {/* Lien HTML simple et non `<Link>` : `react-router-dom` v7 ne se
            résout pas sous Jest, et ce composant doit rester testable. Une
            navigation d'administration par jour ne justifie pas de perdre le
            test. */}
        <a
          href="/dashboard/admin/email-send-log"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
          data-testid="alert-send-log-link"
        >
          <ClockCounterClockwise size={16} />
          Journal des envois
        </a>
      </div>

      <div className="mt-3 space-y-2">
        {(data.allowed_delivery_modes || []).map((mode) => (
          <label
            key={mode}
            className="flex items-start gap-2.5 cursor-pointer group"
            data-testid={`alert-mode-${mode}`}
          >
            <input
              type="radio"
              name="alert-delivery-mode"
              checked={data.delivery_mode === mode}
              disabled={busy}
              onChange={() => setMode(mode)}
              className="mt-1 h-4 w-4 border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]/30"
            />
            <span className="text-sm text-zinc-700 group-hover:text-[#002FA7]">
              {MODE_LABELS[mode] || mode}
            </span>
          </label>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <span className="text-xs text-zinc-600" data-testid="alert-digest-pending">
          {waiting.length
            ? `${waiting.length} évènement(s) en attente du prochain résumé`
            : "Aucun évènement en attente"}
        </span>
        <button
          type="button"
          onClick={sendNow}
          disabled={busy || !waiting.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="alert-digest-send-now"
        >
          <PaperPlaneTilt size={16} />
          Envoyer le résumé maintenant
        </button>
      </div>
    </div>
  );
};

export default AlertDeliveryModeCard;
